import { memo, useCallback, useMemo } from 'react'
import { useSelector } from 'react-redux'
import sanitize from 'sanitize-html'

import { useAppDispatch } from 'cng-common/hooks/useAppDispatch'

import { TranslationMode } from '../../configs'
import { useCheckI18NextInitialization } from '../../hooks/useCheckI18NextInitialization'
import { useTranslatedText } from '../../hooks/useTranslatedText'
import { selectTranslationMode } from '../../selectors'
import { updateTranslations } from '../../thunks/updateTranslations'

import { EditableArea } from './components'
import { replaceKeysInTranslation } from './helpers/replaceKeysInTranslation'
import { ITranslationProps } from './models/translationProps'

const TranslationComponent = ({
  text: key,
  htmlAvailable = true,
  replaceParams,
  span,
  skipInterpolation,
}: ITranslationProps) => {
  const dispatch = useAppDispatch()
  const [translatedText, setTranslatedText] = useTranslatedText(key, skipInterpolation)
  const translationMode = useSelector(selectTranslationMode)
  const editableValue =
    translatedText !== '' && translatedText !== '<p><br></p>' ? translatedText : key

  const readModeValue = useMemo(() => {
    return replaceKeysInTranslation(translatedText, replaceParams)
  }, [translatedText, replaceParams])

  const onClickAccept = useCallback(
    async (value: string) => {
      dispatch(updateTranslations({ key, value }))

      setTranslatedText(value)
    },
    [dispatch, key, setTranslatedText]
  )

  if (translationMode === TranslationMode.Edit) {
    return <EditableArea value={editableValue} onSave={onClickAccept} />
  }

  if (htmlAvailable && span) {
    return <span dangerouslySetInnerHTML={{ __html: sanitize(readModeValue) }} />
  }

  if (htmlAvailable) {
    return <div dangerouslySetInnerHTML={{ __html: sanitize(readModeValue) }} />
  }

  if (span) {
    return <span>{readModeValue}</span>
  }

  return <div>{readModeValue}</div>
}

export const Translation = memo<ITranslationProps>((props) => {
  const isInitialized = useCheckI18NextInitialization()

  if (isInitialized) {
    return <TranslationComponent {...props} />
  }

  return <>{props.text}</>
})

Translation.displayName = 'Translation'
